// Settings ▸ Two-step sign-in (CEO 2026-10-21).
//
//   - status: on since …, recovery codes left, remembered devices
//   - get new recovery codes (needs a current code from the app)
//   - sign out other remembered devices
//   - set it up from here when it's optional (or before the next sign-in)
//   - "almost ready" until the database step + key are in place
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { api } from "@/lib/api";
import {
  AuthenticatorSetup,
  RecoveryCodesPanel,
  sessionCaller,
  twoStepErrorText,
} from "@/auth/TwoStepSignIn";

interface MfaStatus {
  mode: "required" | "optional" | "off";
  ready: boolean;
  reason: "off" | "tables_missing" | "key_missing" | "misconfigured" | null;
  enabled: boolean;
  enabled_at: string | null;
  recovery_codes_left: number;
  remembered_devices: number;
  this_device_remembered: boolean;
}

type Msg = { kind: "success" | "error"; text: string } | null;

function Notice({ msg }: { msg: Msg }) {
  if (!msg) return null;
  const cls = msg.kind === "error" ? "border-rust-300 bg-rust-50 text-rust-600" : "border-peach-400 bg-peach-50 text-brown-500";
  return (
    <div role={msg.kind === "error" ? "alert" : "status"} className={`rounded-md border px-3 py-2 text-[15px] ${cls}`}>
      {msg.text}
    </div>
  );
}

export function TwoStepSettings() {
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const [mode, setMode] = useState<"idle" | "setup" | "regen" | "codes">("idle");
  const [codes, setCodes] = useState<string[]>([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await api<MfaStatus>("/auth/mfa/status"));
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const regenerate = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    setBusy(true);
    try {
      const body = await api<{ recovery_codes: string[] }>("/auth/mfa/recovery-codes", {
        method: "POST",
        body: JSON.stringify({ code: code.trim() }),
      });
      setCodes(body.recovery_codes);
      setMode("codes");
      setCode("");
    } catch (err) {
      setMsg({ kind: "error", text: twoStepErrorText(err) });
    } finally {
      setBusy(false);
    }
  };

  const revokeOthers = async () => {
    setMsg(null);
    setBusy(true);
    try {
      const body = await api<{ devices_signed_out: number }>("/auth/mfa/devices/revoke-others", { method: "POST" });
      setMsg({
        kind: "success",
        text:
          body.devices_signed_out === 0
            ? "No other remembered devices."
            : `Done — ${body.devices_signed_out} other device${body.devices_signed_out === 1 ? "" : "s"} will ask for a code next time.`,
      });
      await load();
    } catch (err) {
      setMsg({ kind: "error", text: twoStepErrorText(err) });
    } finally {
      setBusy(false);
    }
  };

  if (loadFailed) return <Notice msg={{ kind: "error", text: "Couldn't load two-step sign-in. Refresh the page to try again." }} />;
  if (!status) return <p className="text-[15px] text-brown-300">Loading…</p>;

  if (mode === "codes") {
    return (
      <RecoveryCodesPanel
        codes={codes}
        doneLabel="I've saved them"
        onDone={() => {
          setMode("idle");
          setCodes([]);
          setMsg({ kind: "success", text: "Your new recovery codes are ready. The old ones no longer work." });
          void load();
        }}
      />
    );
  }

  if (!status.ready) {
    return (
      <p className="text-[16px] leading-[1.5] text-brown-400">
        {status.reason === "misconfigured"
          ? "Two-step sign-in is misconfigured — contact admin."
          : status.reason === "off"
          ? "Two-step sign-in is switched off for Flow right now."
          : "Two-step sign-in is almost ready. Once it's switched on, Flow will ask you to set it up the next time you sign in."}
      </p>
    );
  }

  if (!status.enabled) {
    if (mode === "setup") {
      return (
        <AuthenticatorSetup
          call={sessionCaller}
          showRemember={false}
          onConfirmed={(body) => {
            setCodes(body.recovery_codes);
            setMode("codes");
          }}
        />
      );
    }
    return (
      <div className="space-y-4">
        <p className="text-[16px] leading-[1.5] text-brown-400">
          {status.mode === "required"
            ? "Not set up yet. Flow will ask you to set it up at your next sign-in — or do it now."
            : "Not set up yet. Each sign-in will also ask for a 6-digit code from your phone."}
        </p>
        <Button onClick={() => setMode("setup")}>Set up two-step sign-in</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1 text-[16px] text-brown-500">
        <p>
          <strong>On</strong>
          {status.enabled_at ? ` since ${new Date(status.enabled_at).toLocaleDateString()}` : ""}. Each sign-in asks for a code from your
          authenticator app.
        </p>
        <p>
          Recovery codes left: <strong>{status.recovery_codes_left} of 10</strong>
          {status.recovery_codes_left <= 3 ? " — get new ones below." : ""}
        </p>
        <p>
          Remembered devices: <strong>{status.remembered_devices}</strong>
          {status.this_device_remembered ? " (including this one)" : ""}
        </p>
      </div>

      {mode === "regen" ? (
        <form onSubmit={regenerate} className="space-y-3">
          <label htmlFor="mfa_regen_code" className="block text-sm font-semibold text-brown-500">
            Type the 6-digit code from your app to get new recovery codes
          </label>
          <Input
            id="mfa_regen_code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            required
          />
          <div className="flex gap-3">
            <Button type="submit" disabled={busy || code.trim().length < 6}>
              {busy ? "Checking…" : "Get new recovery codes"}
            </Button>
            <Button variant="ghost" type="button" onClick={() => setMode("idle")} disabled={busy}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => setMode("regen")}>
            Get new recovery codes
          </Button>
          <Button variant="outline" onClick={revokeOthers} disabled={busy || status.remembered_devices === 0}>
            Sign out other remembered devices
          </Button>
        </div>
      )}

      <Notice msg={msg} />
      <p className="text-[14px] text-brown-300">Lost your phone and your recovery codes? Ask an admin to reset your two-step sign-in.</p>
    </div>
  );
}
